import { EventSource } from "eventsource";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useWorktreeStore } from "@/composables/worktree-manager";
import config from "@/config";
import { api, type types } from "@/lib/openapi";
import { ThreadKind } from "@/lib/openapi/gen";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { notifyEntityChanged } from "@/stores/cache-invalidation";

export type ThreadSchema = types.ThreadSchema;
export type ThreadEntrySchema = types.ThreadEntrySchema;
export type ThreadWithEntriesSchema = types.ThreadWithEntriesSchema;
export type ConflictRecordSchema = types.ConflictRecordSchema;
export type EntityVersionsSchema = types.EntityVersionsSchema;
export type UserChangeSchema = types.UserChangeSchema;

export type ConflictResolution = "keep_main" | "keep_thread" | "edited";

export interface AffectedEntity {
    entity_type: string;
    entity_id: string;
}

/**
 * Outcome of an accept/reject. `conflict` means the accept was gated because the
 * thread is behind main: the backend recorded pick-a-side conflict records during
 * the forced catch-up, the active thread has been refetched so they're now on
 * `conflicts`, and the caller should show the resolution view instead of closing.
 */
export type ResolveOutcome = "resolved" | "conflict" | "error";

/**
 * The OpenAPI client is configured `throwOnError`, so a non-2xx response REJECTS
 * with the parsed error body rather than resolving. A backend DomainError
 * serialises as `{ detail: [{ type, message }] }`; a blocked accept on a
 * behind-main thread surfaces `ErrorType.CONFLICT` (`type === "CONFLICT"`).
 */
interface DomainErrorBody {
    detail: Array<{ type?: unknown; message?: unknown }>;
}

const asDomainErrorBody = (error: unknown): DomainErrorBody | undefined => {
    if (
        error != null &&
        typeof error === "object" &&
        Array.isArray((error as { detail?: unknown }).detail)
    ) {
        return error as DomainErrorBody;
    }
    return undefined;
};

const isConflictError = (error: unknown): boolean =>
    asDomainErrorBody(error)?.detail.some((d) => d.type === "CONFLICT") ??
    false;

const domainErrorMessage = (error: unknown): string | undefined => {
    const message = asDomainErrorBody(error)?.detail[0]?.message;
    return typeof message === "string" ? message : undefined;
};

export const useThreadsStore = defineStore("threads", () => {
    const toast = useToast();
    const threads = ref<ThreadSchema[]>([]);
    const activeThread = ref<ThreadSchema | undefined>(undefined);
    const activeEntries = ref<ThreadEntrySchema[]>([]);
    const isLoading = ref(false);
    const isFetching = ref(false);

    // Driven by SSE agent_started / agent_finished events, and seeded from the thread's
    // has_pending_agent_run on every full fetch (initial load + reconnect catch-up).
    const isWaitingForAgent = ref(false);

    // --- Catch-up conflicts (pick-a-side resolution) ---
    // A thread that is behind main and collided on catch-up carries conflict records;
    // each names the precise entity that needs a pick-a-side resolution before accept
    // can unblock. Exposed off the active thread so the drawer banner + per-conflict
    // rows render straight from store state.
    const conflicts = computed<ConflictRecordSchema[]>(
        () => activeThread.value?.conflicts ?? []
    );
    const pendingConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "pending")
    );
    // Resolved-but-not-accepted records: decisions stay revertible until
    // Accept (869dyyzh9), so the drawer keeps a review path to the Reconcile
    // page as long as any exist.
    const resolvedConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "resolved")
    );
    const hasPendingConflicts = computed(
        () => pendingConflicts.value.length > 0
    );

    // --- Non-agent (user) branch edits ---
    // Entities a user changed directly on the thread branch (NOT the facilitator
    // agent). Surfaced in the resolve dialog so a reviewer sees the non-agent
    // footprint they are about to accept/reject. Lazily fetched (the detection is
    // gitaly-heavy) when the dialog opens, not on every thread poll.
    const userChanges = ref<UserChangeSchema[]>([]);

    // --- Real-time updates via SSE (replaces the old polling loop) ---
    let eventSource: EventSource | null = null;

    const lastSequence = (): number =>
        activeEntries.value.reduce(
            (max, e) => Math.max(max, e.sequence ?? 0),
            0
        );

    const disconnectThread = () => {
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
    };

    const connectToThread = (threadId: string) => {
        // Tear down any previous connection (e.g. switching threads).
        disconnectThread();

        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        const source = new EventSource(
            `${config.bekendURI}/thread/events/${threadId}`,
            {
                // Resolve the token *inside* the fetch override: the eventsource lib reuses
                // this closure on every auto-reconnect, and the SSE endpoint re-authorizes on
                // each reconnect. Firebase ID tokens expire ~1h, so capturing once would let
                // the stream silently 401 after expiry. getToken() returns a cached token and
                // auto-refreshes near expiry, so this stays cheap.
                fetch: async (input, init) => {
                    const token = await getToken();
                    return fetch(input, {
                        ...init,
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${token}`,
                            Organization: organization?.value?.identity ?? "",
                            Stek: stek?.value?.identity ?? "",
                        },
                    });
                },
            }
        );
        eventSource = source;

        // Fires on initial connect and on every auto-reconnect — full fetch to catch up
        // on anything missed while disconnected (pub/sub is fire-and-forget).
        source.addEventListener("open", () => {
            void fetchThread(threadId);
        });

        source.addEventListener("message", ({ data }) => {
            let event: { type?: string };
            try {
                event = JSON.parse(data);
            } catch {
                return;
            }
            switch (event.type) {
                case "new_entry":
                    void fetchNewEntries(threadId);
                    break;
                case "agent_started":
                    isWaitingForAgent.value = true;
                    break;
                case "agent_finished":
                    isWaitingForAgent.value = false;
                    // The agent may have mutated entities on the branch; SSE only
                    // refreshes the chat, so re-sync the worktree to surface them live.
                    void useWorktreeStore().sync();
                    break;
                case "thread_resolved":
                    void fetchThread(threadId);
                    void fetchThreads();
                    disconnectThread();
                    break;
            }
        });

        // EventSource auto-reconnects on transient errors (and 'open' re-fires, triggering a
        // catch-up fetch), but it does NOT reconnect on a fatal response such as a 403 — so we
        // log without claiming a retry, making a permanent failure visible rather than letting
        // updates silently stall. (A user-facing reconnecting/disconnected indicator that can
        // distinguish the two states would be a good follow-up.)
        source.addEventListener("error", () => {
            console.warn(
                `Thread SSE connection error (thread ${threadId}); ` +
                    `the browser reconnects if it was transient.`
            );
        });
    };

    /**
     * Map from entity identity to the open threads that affect it.
     * Only includes open threads.
     */
    const entityThreadMap = computed(() => {
        const map = new Map<string, ThreadSchema[]>();
        for (const thread of threads.value) {
            if (thread.status !== "open") continue;
            const affected =
                (
                    thread as ThreadSchema & {
                        affected_entities?: AffectedEntity[];
                    }
                ).affected_entities ?? [];
            for (const ae of affected) {
                const existing = map.get(ae.entity_id) ?? [];
                existing.push(thread);
                map.set(ae.entity_id, existing);
            }
        }
        return map;
    });

    const getThreadsForEntity = (entityId: string): ThreadSchema[] => {
        return entityThreadMap.value.get(entityId) ?? [];
    };

    const hasThreadsForEntity = (entityId: string): boolean => {
        return entityThreadMap.value.has(entityId);
    };

    // Drawer state
    const drawerOpen = ref(false);
    const drawerThreadId = ref<string | undefined>(undefined);

    const openDrawer = (threadId?: string) => {
        drawerOpen.value = true;
        if (threadId) {
            drawerThreadId.value = threadId;
        }
    };

    /**
     * Open the drawer on the threads *list* (no thread selected). Used by the
     * entity discussion indicators: clicking one shows the list of threads
     * rather than jumping straight into a thread's branch. Clears any
     * previously selected thread so a stale `drawerThreadId` doesn't reopen the
     * detail view; the active branch view (if any) is left untouched.
     */
    const openThreadsList = () => {
        drawerThreadId.value = undefined;
        drawerOpen.value = true;
    };

    const closeDrawer = () => {
        drawerOpen.value = false;
    };

    const selectThread = (threadId: string | undefined) => {
        drawerThreadId.value = threadId;
    };

    const fetchThreads = async () => {
        isFetching.value = true;
        const { data, response } =
            await api.startThreadThreadMgtListThreadsStartPost({});

        if (response.ok && data) {
            threads.value = data.threads;
        } else {
            toast.add({
                severity: "error",
                summary: "Could not load threads",
                detail: "Server returned " + response.status,
            });
        }
        isFetching.value = false;
    };

    const fetchThread = async (threadId: string) => {
        isFetching.value = true;
        const { data, response } =
            await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                },
            });

        if (response.ok && data) {
            activeThread.value = data.thread;
            activeEntries.value = data.entries ?? [];
            // Seed the agent indicator; SSE events take over from here.
            isWaitingForAgent.value =
                data.thread?.has_pending_agent_run ?? false;
        } else {
            toast.add({
                severity: "error",
                summary: "Could not load thread",
                detail: "Server returned " + response.status,
            });
        }
        isFetching.value = false;
    };

    /**
     * Delta fetch: pull only entries newer than what we already have and append them.
     * Triggered by a `new_entry` SSE notification — keeps bandwidth proportional to new
     * activity instead of refetching the whole thread.
     */
    const fetchNewEntries = async (threadId: string) => {
        const { data, response } =
            await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                    since_sequence: lastSequence(),
                },
            });

        if (!response.ok || !data) return;
        // Ignore late deltas for a thread we've since navigated away from.
        if (activeThread.value?.identity !== threadId) return;

        activeThread.value = data.thread;
        const existing = new Set(activeEntries.value.map((e) => e.id));
        const fresh = (data.entries ?? []).filter((e) => !existing.has(e.id));
        if (fresh.length) {
            activeEntries.value = [...activeEntries.value, ...fresh];
        }
    };

    /**
     * Catch the thread's branch up with main — the open/view interaction trigger
     * (reconciliation model §4). Merges current master into the branch so a forked
     * domain reads fresh and the diff badges are accurate, and a blocked catch-up
     * records conflicts (surfaced via the behind-main banner). Best-effort: a
     * failure must never block viewing the thread, so errors are swallowed. On
     * success the thread is refetched (to surface any newly-recorded conflicts) and
     * the worktree re-synced (to refresh the diff badges) so the view reflects the
     * post-catch-up state.
     */
    const catchUpThread = async (threadId: string) => {
        try {
            await api.startThreadThreadMgtCatchUpStartPost({
                body: { thread_id: threadId },
            });
        } catch (error) {
            console.warn(
                `Could not catch thread ${threadId} up with main`,
                error
            );
            return;
        }
        // Refresh unless the user has since switched to a different thread (don't
        // clobber their current view); refetch on first open (activeThread unset).
        if (!activeThread.value || activeThread.value.identity === threadId) {
            await fetchThread(threadId);
        }
        void useWorktreeStore().sync();
    };

    /**
     * Answer a proposal. Confirm runs the saved plan right away on the backend
     * (falls back to an agent run if it can't); Cancel just records the answer.
     */
    const respondToProposal = async (
        threadId: string,
        entryId: string,
        response: "confirm" | "cancel"
    ) => {
        try {
            await api.startThreadThreadMgtRespondToProposalStartPost({
                body: {
                    thread_id: threadId,
                    entry_id: entryId,
                    response,
                },
            });
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not answer the proposal",
                detail: "It may already be answered — refreshing.",
                life: 4000,
            });
        }
        await fetchThread(threadId);
    };

    // Best-effort "user is typing" ping so the agent holds off while composing.
    const notifyTyping = async (threadId: string) => {
        try {
            await api.notifyTypingThreadTypingThreadIdPost({
                path: { thread_id: threadId },
            });
        } catch (e) {
            console.warn(`Typing ping failed for thread ${threadId}`, e);
        }
    };

    const createThread = async (
        title: string,
        description?: string,
        kind?: types.ThreadKind
    ) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtCreateThreadStartPost({
                body: {
                    title,
                    description: description ?? null,
                    kind: kind ?? null,
                },
            });

        if (response.ok && data) {
            await fetchThreads();
            toast.add({
                severity: "success",
                summary: "Thread created",
                detail: data.title,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not create thread",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    // Creating a strategy-onboarding thread wakes the agent so it opens the chat.
    const startStrategyOnboarding = async () => {
        const data = await createThread(
            "Draft Vision & Mission",
            "Set up our Vision & Mission living document with the assistant.",
            ThreadKind.STRATEGY_ONBOARDING
        );
        if (data?.identity) {
            openDrawer(data.identity);
        }
        return data;
    };

    const contribute = async (threadId: string, content: string) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtContributeToThreadStartPost({
                body: {
                    thread_id: threadId,
                    content,
                    source_type: "user",
                },
            });

        if (response.ok && data) {
            await fetchThread(threadId);
        } else {
            toast.add({
                severity: "error",
                summary: "Could not contribute",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    const resolveThread = async (
        threadId: string,
        action: "accept" | "reject",
        reason: string
    ): Promise<ResolveOutcome> => {
        isLoading.value = true;
        try {
            await api.startThreadThreadMgtResolveThreadStartPost({
                body: {
                    thread_id: threadId,
                    action,
                    reason,
                },
            });
            // Clean accept/reject: refresh the active thread + list so the UI
            // reflects the resolved status.
            await fetchThread(threadId);
            await fetchThreads();
            toast.add({
                severity: "success",
                summary: `Thread ${action}ed`,
                detail: reason,
                life: 3000,
            });
            return "resolved";
        } catch (error) {
            // A behind-main thread gates accept with a CONFLICT DomainError: the
            // backend already recorded the pick-a-side conflict records via the
            // forced catch-up, so refetch the thread to surface them (they ride on
            // `activeThread.conflicts` → the "Behind main" banner + resolution
            // panel) and route the caller into the resolution view. A reject's
            // partially-merged CONFLICT is NOT a pick-a-side case — fall through to
            // the error toast so it's surfaced rather than swallowed.
            if (action === "accept" && isConflictError(error)) {
                await fetchThread(threadId);
                return "conflict";
            }
            toast.add({
                severity: "error",
                summary: `Could not ${action} thread`,
                detail: domainErrorMessage(error) ?? "Unexpected error",
            });
            return "error";
        } finally {
            // Always clear loading — a thrown CONFLICT used to leave the spinner
            // hanging forever.
            isLoading.value = false;
        }
    };

    /**
     * Read a conflicted entity's content at base / master / branch for the
     * pick-a-side 3-way diff. Returns the three sides (or undefined on failure).
     */
    const readEntityVersions = async (
        threadId: string,
        entityType: string,
        entityId: string
    ): Promise<EntityVersionsSchema | undefined> => {
        const { data, response } =
            await api.startThreadThreadMgtReadEntityVersionsStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                },
            });

        if (response.ok && data) {
            return data;
        }
        toast.add({
            severity: "error",
            summary: "Could not load entity versions",
            detail: "Server returned " + response.status,
        });
        return undefined;
    };

    /**
     * Fetch the entities a user (not the agent) edited directly on the thread
     * branch, and cache them on `userChanges`. Called when the resolve dialog
     * opens so its "Changes made directly by users" section reflects the branch.
     * Returns the list (empty on failure or when there are no user edits).
     */
    const fetchUserChanges = async (
        threadId: string
    ): Promise<UserChangeSchema[]> => {
        const { data, response } =
            await api.startThreadThreadMgtListThreadUserChangesStartPost({
                body: { thread_id: threadId },
            });

        if (response.ok && data) {
            userChanges.value = data;
            return data;
        }
        userChanges.value = [];
        toast.add({
            severity: "error",
            summary: "Could not load user changes",
            detail: "Server returned " + response.status,
        });
        return [];
    };

    /**
     * Resolve one entity's catch-up conflict by picking a side (keep_main /
     * keep_thread / edited). Refetches the thread so the banner + per-conflict
     * rows reflect the new status (and accept unblocks once none stay pending).
     */
    const resolveConflict = async (
        threadId: string,
        entityType: string,
        entityId: string,
        resolution: ConflictResolution,
        content?: string
    ): Promise<ConflictRecordSchema | undefined> => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtResolveConflictStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                    resolution,
                    content: content ?? null,
                },
            });

        if (response.ok && data) {
            await fetchThread(threadId);
            // The branch entity now holds the chosen version (keep_main /
            // keep_thread / edited). Refresh the viewed entity + worktree so it
            // updates live on the branch — without this, the description stays on
            // the pre-resolution value until a manual page refresh.
            notifyEntityChanged([{ entityType, entityId }]);
            void useWorktreeStore().sync();
            toast.add({
                severity: "success",
                summary: "Conflict resolved",
                detail: data.name || entityId,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not resolve conflict",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return response.ok ? data : undefined;
    };

    return {
        threads,
        activeThread,
        activeEntries,
        conflicts,
        pendingConflicts,
        resolvedConflicts,
        hasPendingConflicts,
        userChanges,
        isLoading,
        isFetching,
        isWaitingForAgent,
        drawerOpen,
        drawerThreadId,
        entityThreadMap,
        fetchThreads,
        fetchThread,
        fetchNewEntries,
        createThread,
        startStrategyOnboarding,
        contribute,
        respondToProposal,
        notifyTyping,
        resolveThread,
        readEntityVersions,
        resolveConflict,
        fetchUserChanges,
        catchUpThread,
        connectToThread,
        disconnectThread,
        openDrawer,
        openThreadsList,
        closeDrawer,
        selectThread,
        getThreadsForEntity,
        hasThreadsForEntity,
    };
});

export const useThreadsManager = () => storeToRefs(useThreadsStore());
